import { Label } from '@/features/label/components/label';
import { LabelGroup } from '@/features/label/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PricePeriodSettings } from '../components/price-period-settings';
import { Separator } from '@/components/ui/separator';
import { useSettings } from '../api/getSettings';
import { DeadlineSettings } from '../components/deadline-settings';
import { EmailSettings } from '../components/email-settings';
import { Calendar, CalendarRange, ChevronsUpDown, DollarSign, Mail, MessageCircle } from 'lucide-react';
import { YearSettings } from '../components/year-settings';
import { CalendarDate } from 'react-bootstrap-icons';
import { EventDaysSettings } from '../components/event-days-settings';
import { GroundLocationSettings } from '../components/ground-location-settings';
import { BannerSettings } from '../components/banner-settings';
import { useState } from 'react';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';

const tabOptions = [
  { value: 'year', label: 'year_settings', icon: CalendarRange },
  { value: 'event_days', label: 'event_days', icon: CalendarDate },
  { value: 'deadline', label: 'DeadlineSettings', icon: Calendar },
  { value: 'price_period', label: 'PricePeriodSettings', icon: DollarSign },
  { value: 'email', label: 'EmailSettings', icon: Mail },
  { value: 'banner', label: 'banner', icon: MessageCircle }
];

const labelForTab = (value: string) => {
  const tab = tabOptions.find(t => t.value === value);
  if (!tab) return null;

  return <Label name={tab.label} groupName={LabelGroup.ADMIN_SETTINGS} />;
};

export function Settings() {
  const { isLoading } = useSettings({});
  const [activeTab, setActiveTab] = useState('year');
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (isLoading) return null;

  return (
    <div className="space-y-6 w-full">
      <div>
        <h2 className="text-xl font-semibold">
          <Label name="Settings" groupName={LabelGroup.ADMIN_SETTINGS} />
        </h2>
        <p className="text-muted-foreground text-sm">
          <Label name="SettingsDescription" groupName={LabelGroup.ADMIN_SETTINGS} />
        </p>
      </div>

      <Separator />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col md:flex-row gap-2 md:gap-10 items-start">
        {/*Mobile select*/}
        <div className="md:hidden w-full bg-white">
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerTrigger asChild>
              <Button className="w-full flex border rounded-md p-3 text-left gap-4">
                {labelForTab(activeTab)} <ChevronsUpDown className="inline-block h-4 w-4" />
              </Button>
            </DrawerTrigger>

            <DrawerContent>
              <div className="p-4 space-y-2">
                {tabOptions.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <Button
                      key={tab.value}
                      className="w-full p-3 text-left rounded-md hover:bg-accent flex items-center gap-2"
                      onClick={() => {
                        setActiveTab(tab.value);
                        setDrawerOpen(false);
                      }}>
                      <Icon className="h-4 w-4" />
                      <Label name={tab.label} groupName={LabelGroup.ADMIN_SETTINGS} />
                    </Button>
                  );
                })}
              </div>
            </DrawerContent>
          </Drawer>
        </div>

        {/*Desktop menu*/}
        <TabsList className="hidden md:flex flex-col bg-transparent [&>div]:h-full  gap-2 h-auto text-black">
          {tabOptions.map(tab => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.value} value={tab.value} className="flex justify-start data-[state=active]:bg-brand py-1 data-[state=active]:text-white w-64">
                <Icon className="mr-2 h-4 w-4" />
                <Label name={tab.label} groupName={LabelGroup.ADMIN_SETTINGS} />
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/*Content*/}
        <TabsContent value="price_period" className="w-full">
          <PricePeriodSettings />
        </TabsContent>
        <TabsContent value="deadline" className="w-full">
          <DeadlineSettings />
        </TabsContent>
        <TabsContent value="email" className="w-full">
          <EmailSettings />
        </TabsContent>
        <TabsContent value="markplatser" className="w-full">
          <GroundLocationSettings />
        </TabsContent>
        <TabsContent value="event_days" className="w-full">
          <EventDaysSettings />
        </TabsContent>
        <TabsContent value="banner" className="w-full">
          <BannerSettings />
        </TabsContent>
        <TabsContent value="year" className="w-full">
          <YearSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
